import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled, {
  createGlobalStyle,
} from 'styled-components';
import Draggable from 'react-draggable';
import CloseIcon from '@material-ui/icons/Close';
import { ClickAwayListener } from '@material-ui/core';

import { ButtonModalClose } from '../atoms';
import { ButtonWithLoader } from './index';
import { useReactToPrint } from 'react-to-print';
import FormsReport from '../pages/Reports/FormsReport';

const ModalDefaultDraggable = ({
  children,
  title,
  onClose,
  className = '',
  addContactModal,
  disabledDraggable,
  centered,
  wide,
  extraWide,
  narrow,
  showPrintButton,
  printRef,
  onClickAwayClose = true,
  zIndex,
  maxHeight,
  maxWidth,
}: Props) => {
  const containerRef = React.useRef(null)
  // Modern iPadOS Safari reports a desktop user-agent, so userAgent sniffing
  // misses it. maxTouchPoints reliably flags touch-primary devices (iPad === 5,
  // desktop === 0). Dragging a modal with a finger is useless on a tablet and
  // hijacks the touch-scroll gesture, so we disable it there.
  const isTouchDevice =
    typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
  const printableRef = printRef ? printRef : React.useRef();
  const handlePrintInspectionForm = useReactToPrint({
    content: () => printableRef.current,
    pageStyle: `
      @page {
        size: auto;
        margin-top: 20mm;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });
  const formExportToPdf = () => {
      handlePrintInspectionForm();
  };
React.useEffect(()=>{  
  if(addContactModal && containerRef && containerRef.current){
    containerRef.current.scrollTo(0,0)
    }
},[addContactModal])


  return ReactDOM.createPortal(
    <ModalContainer ref={containerRef} className={className} addContactModal={addContactModal} style={zIndex ? { zIndex } : undefined}>
      <GlobalStyles />
      <ClickAwayListener
        onClickAway={onClickAwayClose ? onClose : () => {}}
        mouseEvent="onMouseDown"
      >
        <Draggable
        disabled={disabledDraggable || isTouchDevice}
          cancel=".react-draggable-cancel"
          positionOffset={{ x: '-50%', y: centered ? '-50%' : '0%' }}
        >
          <ModalContent
            centered={centered}
            wide={wide}
            extraWide={extraWide}
            narrow={narrow}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
          >
            <StyledButtonModalClose onClick={onClose} onTouchStart={onClose}/>
            <StyledTitle>{title}</StyledTitle>
            {showPrintButton && (
              <ButtonWithLoader
                variant="outlined"
                disabled={false}
                onTouchStart={formExportToPdf}
                onClick={formExportToPdf}
              >
                Print
              </ButtonWithLoader>
            )}
            <ModalContentInner ref={ printRef ? printRef : printableRef}>
              <div>{children}</div>
            </ModalContentInner>
          </ModalContent>
        </Draggable>
      </ClickAwayListener>
    </ModalContainer>,
    document.body
  );
};

const GlobalStyles = createGlobalStyle`
  body {
    max-height: 100vh;
    overflow: hidden;
    padding-right: 17px;

    @media only screen and (max-width: 840px) {
      padding-right: 0;
    }

    @media print {
      table[role="table"] {
        display: none;
      }
    }
  }
`;

const MobileClose = styled.button`
 width: 32px;
 height: 32px;
 border-radius:50%;
 z-index: 300;
 position: absolute;
 right:32px;
 top:32px;
 background-color:#fff;
 @media only screen and (min-width: 840px) {
   display:none;
 }
`;

const CloseWrapper = styled.div`  ;
zoom: 1.2;
position: relative;
left: -5px;
top: -2px;
`;
const ModalContainer = styled.div`
  position: fixed;
  z-index: var(--modalZIndex);
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 40px 20px;
  overflow-x: hidden;
  overflow:${props=>(props.addContactModal?`hidden`:`auto`)};
  display: flex;
  background-color: rgba(0, 0, 0, 0.4);
  ${({ className }) =>
    className === 'over' && 'overflow: visible;'}

  @media only screen and (max-width: 840px) {
    padding: 20px 12px;
  }

  @media only screen and (max-width: 540px) {
    padding: 12px 8px;
  }
`;

const ModalContent = styled.div<Content>`
  background-color: var(--defaultBackground);
  width: ${({ wide, extraWide, narrow }) =>
    // eslint-disable-next-line no-nested-ternary
    narrow ? '480px' : wide ? '1000px' : extraWide ? '95%' : '100%'};
  min-width: 280px;
  max-width: ${({ wide, extraWide, narrow, maxWidth }) => {
    if (maxWidth) return maxWidth;
    // eslint-disable-next-line no-nested-ternary
    return narrow ? '480px' : wide ? '1000px' : extraWide ? '45%' : '800px';
  }};
  padding: 35px;
  border-radius: 5px;
  position: absolute;
  top: ${({ centered }) => (centered ? '50%' : '32px')};
  left: 50%;
  margin-bottom: 100px;
  max-height: ${({ maxHeight }) => maxHeight || 'calc(100vh - 80px)'};
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  box-sizing: border-box;

  @media only screen and (min-width: 1025px) and (max-width: 1366px) {
    max-width: ${({ wide, extraWide, narrow }) => {
      // eslint-disable-next-line no-nested-ternary
      return narrow ? '480px' : wide ? '1000px' : extraWide ? '80vw' : '800px';
    }};
    width: ${({ extraWide }) => (extraWide ? '80vw' : undefined)};
  }

  @media only screen and (max-width: 1024px) {
    width: ${({ narrow }) => (narrow ? '480px' : '92vw')};
    max-width: ${({ narrow }) => (narrow ? '480px' : '92vw')};
  }

  @media only screen and (max-width: 840px) {
    width: ${({ narrow }) => (narrow ? '95vw' : '95vw')};
    max-width: 95vw;
    padding: 22px;
    top: ${({ centered }) => (centered ? '50%' : '16px')};
    margin-bottom: 40px;
    max-height: ${({ maxHeight }) => maxHeight || 'calc(100vh - 40px)'};
  }

  @media only screen and (max-width: 540px) {
    width: 100vw;
    max-width: 100vw;
    min-width: 0;
    padding: 16px 14px;
    top: ${({ centered }) => (centered ? '50%' : '8px')};
    margin-bottom: 20px;
    max-height: ${({ maxHeight }) => maxHeight || 'calc(100vh - 20px)'};
    border-radius: 6px;
  }
`;

const ModalContentInner = styled.div.attrs({
  className: 'react-draggable-cancel',
})`
  width: 100%;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: center;
`;

const StyledTitle = styled.div`
  width: 90%;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin: 0 auto 30px auto;

  @media only screen and (max-width: 840px) {
    font-size: 1.125rem;
    margin: 0 auto 18px auto;
    padding-right: 36px;
    padding-left: 36px;
  }

  @media only screen and (max-width: 540px) {
    font-size: 1rem;
    margin: 0 auto 12px auto;
    padding-right: 32px;
    padding-left: 0;
    text-align: left;
  }
`;

const StyledButtonModalClose = styled(ButtonModalClose)`
  top: 32px;
  right: 32px;

  @media only screen and (max-width: 840px) {
    top: 16px;
    right: 16px;
  }

  @media only screen and (max-width: 540px) {
    top: 12px;
    right: 12px;
  }

  @media print {
    display: none;
  }
`;

interface Props {
  children: React.ReactNode | React.ReactNodeArray;
  title?: string;
  onClose: () => void;
  className?: string;
  centered?: boolean;
  wide?: boolean;
  extraWide?: boolean;
  narrow?: boolean;
  showPrintButton?: boolean;
  zIndex?: number;
  maxHeight?: string;
  maxWidth?: string;
}

interface Content {
  centered?: boolean;
  wide?: boolean;
  extraWide?: boolean;
  narrow?: boolean;
  maxHeight?: string;
  maxWidth?: string;
}

export default ModalDefaultDraggable;
